import React from 'react';
import { cn } from '~/lib/utils';
import styles from './BillingCreditSlider.module.css';
import { settingsThemeClass, type HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** The thumb's minimum width, in px — its trailing edge on the first stop. See the stylesheet. */
const MIN_THUMB = 46;
/** The track's own inset at both ends. */
const TRACK_INSET = 4;

export interface BillingCreditSliderProps {
  /**
   * The ladder, low to high. The number of stops is the design's only real variable here: B1/B2/B4
   * draw six, B3 draws seven and B2's top-up card draws five, and the tick spacing falls out of it.
   */
  stops: readonly number[];
  /** The selected stop. A value that is not on the ladder snaps to the nearest one. */
  value: number;
  /** Omit and the slider is read-only — it still draws, it just cannot be moved. */
  onChange?: (value: number) => void;
  /** Accessible name, e.g. "Credits a month". Required: the thumb's number is not one. */
  label: string;
  /**
   * How the thumb prints its value, and how `aria-valuetext` reads. Defaults to grouped digits,
   * which is what B2's thumb draws ("1,000").
   */
  formatValue?: (value: number) => string;
  theme?: HeaderTheme;
  className?: string;
}

const groupDigits = (value: number) => value.toLocaleString('en-US');

/** The stop whose value is closest to `value`, so a caller cannot hand us an index of -1. */
function nearestIndex(stops: readonly number[], value: number): number {
  let best = 0;
  for (let i = 1; i < stops.length; i += 1) {
    if (Math.abs(stops[i] - value) < Math.abs(stops[best] - value)) best = i;
  }
  return best;
}

/**
 * The credit ladder: a 48px trough, a tick per stop, and a thumb whose trailing edge lands on the
 * stop you are on.
 *
 * It is a bar, not a knob. The thumb grows from the track's left inset rather than sliding along it,
 * so the amount you are buying is a length as well as a number — which is why the number rides
 * inside it and the grab bar sits on the trailing edge.
 *
 * **The tick geometry, derived from all five sliders on the boards.** The thumb's trailing edge is
 * 50 on the first stop and 340 on the last — 46 and 336 of thumb inside a 344 track — and the stops
 * divide that 290px evenly. B1 and B4 draw five ticks 58 apart (six stops), B3 draws six ticks 48.2
 * apart (seven stops), and B2's top-up card draws four ticks 72.3 apart (five stops). Every one of
 * those is `290 / (stops - 1)`, and every thumb on every board lands on a tick: 220 wide on B1's
 * fourth stop, 278 on B2's fifth, 104 on B4's second, 46 on B3's first.
 *
 * The LAST stop never gets a tick. Its position is the track's own inner right edge, where a 1px
 * hairline sits under the 12px corner and reads as a rendering artefact rather than as a stop —
 * Paper omits it on all five sliders, which is what makes "ticks" one fewer than "stops" below.
 */
export function BillingCreditSlider({
  stops,
  value,
  onChange,
  label,
  formatValue = groupDigits,
  theme: themeProp,
  className,
}: BillingCreditSliderProps) {
  const theme = useV15Theme(themeProp);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = React.useState(false);

  const index = nearestIndex(stops, value);
  const last = Math.max(1, stops.length - 1);
  const readOnly = !onChange;

  const commit = React.useCallback(
    (next: number) => {
      const clamped = Math.min(stops.length - 1, Math.max(0, next));
      if (onChange && stops[clamped] !== stops[index]) onChange(stops[clamped]);
    },
    [index, onChange, stops],
  );

  /** Pointer x -> the nearest stop, in the same geometry the stylesheet lays the ticks out in. */
  const indexFromPointer = React.useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return index;
      const rect = track.getBoundingClientRect();
      const span = rect.width - TRACK_INSET * 2 - MIN_THUMB;
      if (span <= 0) return index;
      const t = (clientX - rect.left - TRACK_INSET - MIN_THUMB) / span;
      return Math.round(Math.min(1, Math.max(0, t)) * last);
    },
    [index, last],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    commit(indexFromPointer(event.clientX));
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    commit(indexFromPointer(event.clientX));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const step: Record<string, number> = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 };
    if (event.key in step) {
      event.preventDefault();
      commit(index + step[event.key]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      commit(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      commit(stops.length - 1);
    }
  };

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label={label}
      aria-valuemin={stops[0]}
      aria-valuemax={stops[stops.length - 1]}
      aria-valuenow={stops[index]}
      aria-valuetext={formatValue(stops[index])}
      aria-readonly={readOnly || undefined}
      // -1 rather than no attribute at all: a slider that cannot be changed should not be a tab
      // stop, but the role still has to be focusable so a screen reader can land on it and read the
      // amount out. Dropping it also trips `jsx-a11y/interactive-supports-focus`, which cannot see
      // that a conditional value is always present.
      tabIndex={readOnly ? -1 : 0}
      className={cn(
        settingsThemeClass(theme),
        styles.track,
        readOnly && styles.readOnly,
        dragging && styles.dragging,
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
    >
      {stops.slice(0, -1).map((stop, i) => (
        <span
          key={stop}
          className={styles.tick}
          aria-hidden
          style={{ '--bv15-tick-t': String(i / last) } as React.CSSProperties}
        />
      ))}
      <span
        className={styles.thumb}
        style={{ '--bv15-thumb-t': String(index / last) } as React.CSSProperties}
      >
        <span className={styles.amount}>{formatValue(stops[index])}</span>
        <span className={styles.grip} aria-hidden />
      </span>
    </div>
  );
}
